import type { JsonObject, SupraLockboxJsonType } from '@/app-package';
import { CachedOrg } from '@bb/automaton';
import { Pronoun } from '@bb/contacts';
import {
  SupraSystemSubscriptionJsonType,
  SupraSystemSubscriptionStatus,
} from '@bb/domain/dist/types';
import { PopulatedUser } from 'api/UserController/getProfile';
import { getProfile } from 'api/UserController';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import errorReporter from '@app/errorReporter';
import { message } from 'antd';
import { useParams } from 'react-router-dom';
import supraSystemLogo from '../../../img/SupraSystemLogo.png';
import brokerBayLogo from '../../../img/BrokerBayLogo.png';

export type MLSUserPageContextType = {
  loading: boolean;
  user: JsonObject<
    PopulatedUser & { org: CachedOrg; pronouns?: Pronoun[] }
  > | null;
  userId: string | null;
  lockboxes: (SupraLockboxJsonType & { mlsOwnerName?: string })[];
  lockboxesCount: number;
  subscriptions: {
    answering: {
      status: 'Active' | 'Unsubscribed';
      logo: string;
      name: string;
    };
    supra: Partial<
      SupraSystemSubscriptionJsonType & {
        logo: string;
        name: string;
        supraOwnerName: string;
      }
    >;
  };
  isAutoCoopDisabled: boolean | undefined;
  notes: string;
  fetchUser: () => Promise<void>;
  setLoading: (loading: boolean) => void;
  setUser: (user: MLSUserPageContextType['user']) => void;
  setUserId: (id: string) => void;
  setLockboxes: (lockboxes: MLSUserPageContextType['lockboxes']) => void;
  setLockboxesCount: (count: number) => void;
  setSubscriptions: (
    subscriptions: MLSUserPageContextType['subscriptions'],
  ) => void;
  setIsAutoCoopDisabled: (value: boolean | undefined) => void;
  setNotes: (notes: string) => void;
};

const MLSUserPageContext = createContext<MLSUserPageContextType>(null);

export const MLSUserPageContextProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<MLSUserPageContextType['user']>(null);
  const params = useParams<{ userId?: string }>();
  const [userId, setUserId] = useState<string>(params?.userId || '');
  const [lockboxes, setLockboxes] = useState<
    MLSUserPageContextType['lockboxes']
  >([]);
  const [lockboxesCount, setLockboxesCount] = useState(0);
  const [subscriptions, setSubscriptions] = useState<
    MLSUserPageContextType['subscriptions']
  >({
    answering: {
      logo: brokerBayLogo,
      name: 'Answering Service',
      status: 'Unsubscribed',
    },
    supra: {
      logo: supraSystemLogo,
      name: 'Supra System',
      status: SupraSystemSubscriptionStatus.unsubscribed,
    },
  });
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isAutoCoopDisabled, setIsAutoCoopDisabled] = useState<boolean>();

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      if (userId) {
        const retrievedUser = await getProfile(userId, {
          populateOrg: true,
          populateUserSubscriptions: true,
          populateTeam: true,
          includeSubscriptionHistory: true,
          populateAdminNotes: true,
        });
        setUser(retrievedUser);
        setIsAutoCoopDisabled(retrievedUser.isAutoCoopDisabled);
      }
    } catch (err) {
      errorReporter.error(`Error fetching user ${userId} on MLSUserPage`);
      message.error('Error fetching user');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    (async () => {
      await fetchUser();
    })();
  }, [fetchUser]);

  useEffect(() => {
    if (user && user.id && user.subscriptions) {
      const { answeringSubscription, supraSystemSubscription } =
        user.subscriptions;

      const temp: MLSUserPageContextType['subscriptions'] = {
        answering: {
          logo: '../../../../img/BrokerBayLogo.png',
          name: 'Answering Service',
          status: answeringSubscription ? 'Active' : 'Unsubscribed',
        },
        supra: {
          logo: supraSystemLogo,
          name: 'Supra System',
          status:
            supraSystemSubscription?.status ||
            SupraSystemSubscriptionStatus.unsubscribed,
          invitationDate: supraSystemSubscription?.invitationDate || null,
          keySerialNumber: supraSystemSubscription?.keySerialNumber || null,
          type: supraSystemSubscription?.type,
          ...supraSystemSubscription,
        },
      };

      setSubscriptions(temp);
    }

    if (user && user.id && user.adminNotes) {
      setNotes(user.adminNotes);
    }
  }, [user]);

  return (
    <MLSUserPageContext.Provider
      value={{
        fetchUser,
        user,
        userId,
        lockboxes,
        lockboxesCount,
        subscriptions,
        isAutoCoopDisabled,
        notes,
        loading,
        setUser,
        setUserId,
        setLockboxes,
        setSubscriptions,
        setIsAutoCoopDisabled,
        setNotes,
        setLockboxesCount,
        setLoading,
      }}
    >
      {children}
    </MLSUserPageContext.Provider>
  );
};

export default MLSUserPageContext;
